"use client";

import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { renderToStaticMarkup } from "react-dom/server";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { useMembership } from "@/lib/hooks/useMembership";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";
import { renderRichText } from "@storyblok/react";
import type { InterviewStoryblok } from "@/component-types-sb";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Separator } from "@/components/ui/separator";
import { Subline } from "@/components/typography/Subline";
import { Headline } from "@/components/typography/Headline";
import { ImageCard } from "@/components/layout/ImageCard";
import { RichText } from "@/components/typography/RichText";
import { VideoSkeleton } from "@/components/layout/Skeleton";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import InfoBox from "@/components/storyblok/InfoBox";
import Ad from "@/components/storyblok/Ad";
import { trackEvent } from "@/lib/analytics";
import { formatDate } from "@/lib/utils/date";
import { cn } from "@/lib/utils";
import { setReturnIntent } from "@/lib/auth/return-intent";
import { useConsent } from "@/lib/hooks/useConsent";
import { useIntersectionObserver } from "@/lib/hooks/useIntersectionObserver";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <VideoSkeleton className="w-full aspect-video mb-8" />,
});

type InterviewProps = {
  blok: InterviewStoryblok;
  createdAt: string;
};

const Interview = ({ blok, createdAt }: InterviewProps) => {
  const [interviews, setInterviews] = useState<InterviewStoryblok[]>([]);
  const pathname = usePathname();
  const { userId } = useAuth();
  const { isMember } = useMembership();
  const accessDenied = (blok as any)._access_denied === true;
  const isLoggedIn = !!userId;
  const { statistics } = useConsent();
  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
  });

  useEffect(() => {
    trackEvent("content_viewed", {
      type: "interview",
      title: blok.title,
      slug: pathname,
    });
  }, [blok.title, pathname]);

  // Ueber den gecachten Proxy statt ueber die "use server"-Action.
  // Dependency `blok._uid` statt `blok` — siehe Blog.tsx.
  useEffect(() => {
    const controller = new AbortController();

    fetchStoryblokList<InterviewStoryblok>(
      {
        content_type: "interview",
        per_page: 100,
        filter_query: { _uid: { not_in: blok._uid } },
      },
      controller.signal,
    )
      .then(setInterviews)
      .catch((err) => {
        if (err?.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, [blok._uid]);

  return (
    <div
      className="flex justify-between px-4 pt-8 pb-16 lg:pt-16 lg:pb-24 mx-auto max-w-screen-xl"
      {...storyblokEditable(blok)}
    >
      <article className="mx-auto w-full max-w-5xl format format-sm sm:format-base lg:format-lg format-blue text-black-100">
        <header className="mb-4 lg:mb-6 not-format">
          <Subline>
            <time dateTime={createdAt}>{formatDate(new Date(createdAt))}</time>
          </Subline>
          <Headline as="h1" size="huge" className="mb-4 lg:mb-6">
            {blok.title}
          </Headline>

          {blok.member_access === "member_only" && (
            <div className="flex items-center gap-2 flex-wrap">
              <MemberLockIcon locked={accessDenied} />
            </div>
          )}
        </header>

        <p className="text-lead mb-8">{blok.subtitle}</p>

        {!accessDenied && blok.playback_id ? (
          <MuxPlayer
            streamType="on-demand"
            playbackId={blok.playback_id}
            {...(blok.thumbnail?.filename && { poster: blok.thumbnail.filename })}
            {...(blok.thumbnail_time && { thumbnailTime: blok.thumbnail_time })}
            metadataVideoTitle={blok.title}
            metadataViewerUserId={userId || undefined}
            preload="auto"
            className="aspect-video mb-8"
            disableCookies={!statistics}
            accentColor="#fbc02d"
          />
        ) : blok.image?.filename ? (
          <Image
            src={blok.image.filename}
            alt={blok.image.alt || ""}
            width={1280}
            height={720}
            className="mb-4"
          />
        ) : null}

        {blok.header?.map((nestedBlok) => (
          <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}

        <div className={cn(accessDenied && "relative overflow-hidden")}>
          <RichText
            html={renderRichText(blok.text, {
              resolver: (component, blok) => {
                switch (component) {
                  case "info_box":
                    return renderToStaticMarkup(<InfoBox blok={blok} />);
                  case "ad":
                    if (isMember) return "";
                    return renderToStaticMarkup(<Ad blok={blok} />);
                }
              },
            })}
          />

          {accessDenied && (
            <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-sand-10 to-transparent pointer-events-none" />
          )}
        </div>

        {accessDenied && (
          <div id="member-cta" className="not-prose flex flex-col items-center gap-4 py-10 px-6 my-8 bg-sky-50 rounded-3xl text-center">
            <MemberLockIcon locked />
            <h3 className="text-headline-xl text-black">
              Dieses Interview ist exklusiv für Mitglieder
            </h3>
            <p className="text-copy text-black">
              Als Dialetics-Mitglied erhältst du Zugang zu allen TopicTalks, Sprechstunden, Community Weeks und vielen weiteren Events. Dazu kommen exklusive Artikel, Aufzeichnungen und eine Community, die dich versteht.
            </p>
            <Button variant="sun" asChild>
              <Link
                href={isLoggedIn ? "/onboarding/membership" : `/login?redirect=${encodeURIComponent(pathname)}`}
                onClick={() => { if (!isLoggedIn) setReturnIntent(pathname, "interview_access"); }}
              >
                Mitglied werden
                <ArrowRight />
              </Link>
            </Button>
            <Link
              href="/membership"
              className="text-copy text-black hover:opacity-70 transition-opacity"
            >
              Mehr zur Membership
            </Link>
          </div>
        )}

        {!accessDenied && blok.sponsor && (
          <>
            <Separator className="my-4" />
            <h4 className="text-sm font-medium leading-none mb-4">Sponsor</h4>

            <div ref={ref}>
              {blok.logo?.filename && (
                <Link
                  href={blok.link?.url || "#"}
                  target={blok.link?.target || "_self"}
                >
                  <Image
                    src={blok.logo.filename}
                    alt={blok.logo.alt || ""}
                    width={300}
                    height={300}
                    className="float-end ml-4 mb-4 w-full sm:w-auto"
                  />
                </Link>
              )}
              <RichText html={renderRichText(blok.sponsor)} />
            </div>
          </>
        )}

        <Headline as="p" size="tall" className="py-8">
          Weitere Interviews
        </Headline>

        <Carousel>
          <CarouselContent>
            {interviews?.map((nestedBlok) => (
              <CarouselItem className="md:basis-1/3" key={nestedBlok.id}>
                <ImageCard
                  key={nestedBlok.uuid}
                  tagline={nestedBlok.content?.topic}
                  headline={nestedBlok.content?.title}
                  image={nestedBlok.content?.image?.filename}
                  href={`/${nestedBlok.full_slug}`}
                  tags={nestedBlok.content?.tags}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </article>
    </div>
  );
};

export default Interview;
